import React, { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Grid, Card, CardContent, Dialog, Button, IconButton, Tooltip, DialogActions, DialogContent, Switch, FormControlLabel } from "@material-ui/core";
import { Delete, ArrowBack, Add, CheckCircle, FindReplace, Replay } from '@material-ui/icons';
import { withStyles, makeStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import FileUpload from "./Upload";
import ImageContainer from "./ImageContainer";
import { navPath } from "../../constants";
import { HVHeader } from "../shared/DesignSystem";

const ColorButton = withStyles((theme) => ({
  root: {
    color: theme.palette.getContrastText(grey[900]),
    backgroundColor: grey[900],
    "&:hover": {
      backgroundColor: grey[900],
    },
    height: 50,
    minWidth: 100,
  },
}))(Button);

const useStyles = makeStyles({
  root: {
    minWidth: 250,
    marginBottom: 100
  },
  cards: {
    marginTop: 50,
    marginLeft: 25
  },
});

function ClassificationProject(props) {
  const { groupId, projectId } = useParams();
  const classes = useStyles();
  const [status, setStatus] = useState("");
  const [uploadType, setUploadType] = useState('')
  const [openUpload, setOpenUpload] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [tbDeleted, setTBDelete] = useState('');
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [pending, setPending] = useState(false)
  const { enqueueSnackbar } = useSnackbar();
  const [classificationType, setClassificationType] = useState("");
  const [projectName, setProjectName] = useState("");
  const [groupName, setGroupName] = useState("");

  async function getGroupStatus() {
    try {
      const response = await axios.post("/api/groups/status", { groupId: groupId });
      const type = response.data.classificationType;
      setClassificationType(type);
      setGroupName(response.data.name || groupId);

      const projects = response.data.projects || [];
      const project = projects.find(p => p._id === projectId);
      if (project) setProjectName(project.name);
    } catch (e) {
      console.log("Error fetching group status", e);
    }
  }

  async function getImages(type = classificationType) {
    if (!type) return;
    try {
      setLoading(true)
      const response = await axios.post("/api/classification/images/list", {
        projectId: projectId,
        groupId: groupId,
        classificationType: type,
      });
      if (response && response.data) {
        setImages(response.data.images);
        setStatus(response.data.status)
        setLoading(false);
      }
    } catch (e) {
      enqueueSnackbar('Error fetching images', {
        variant: "error",
      });
      setLoading(false);
    }
  }

  async function deleteImage() {
    try {
      const response = await axios.post("/api/classification/images/delete", {
        key: tbDeleted,
      });
      if (response && response.data) {
        enqueueSnackbar(response.data, {
          variant: "success",
        });
        handleCloseDelete();
        getImages();
      }
    } catch (e) {
      enqueueSnackbar('Could not delete image', {
        variant: "error",
      });
    }
  }

  async function refreshOcr() {
    try {
      const response = await axios.post("/api/classification/ocr/redo", {
        projectId: projectId,
        groupId: groupId,
        classificationType: classificationType,
      });
      if (response && response.data) {
        enqueueSnackbar('OCR refresh started', {
          variant: "success",
        });
      }
    } catch (e) {
      enqueueSnackbar('Error fetching images', {
        variant: "error",
      });
      setLoading(false);
    }
  }

  const handleClickOpen = (type) => {
    setUploadType(type);
    setOpenUpload(true);
  };

  const handleDeleteDialog = (key) => {
    setTBDelete(key);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setOpenUpload(false);
  };

  const handleCloseDelete = () => {
    setTBDelete('')
    setOpenDelete(false);
  };

  const handleGoBack = () => {
    props.history.push(navPath(`/classification/${groupId}`));
  };
  const togglePending = (event) => {
    setPending(event.target.checked)
  };

  useEffect(() => {
    if (groupId) {
      getGroupStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (classificationType && projectId) {
      getImages(classificationType);
    }
  }, [classificationType, projectId]);

  return (
    <div style={{ background: '#fafafa', minHeight: '100vh' }}>
      <HVHeader tab="groups" showTabs={false} role={{}} env="STAGING" onLogout={() => props.history.push('/login')} />
      <div style={{ background: '#fff', borderBottom: '1px solid #ececec', padding: '14px 40px' }}>
        <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <Button variant="outlined" size="small" onClick={handleGoBack} startIcon={<ArrowBack />}>Back</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#666' }}>
            <span>Groups</span>
            <span>/</span>
            <span style={{ cursor: 'pointer' }} onClick={() => props.history.push(navPath(`/group/${groupId}`))}>{groupName || groupId}</span>
            <span>/</span>
            <span style={{ cursor: 'pointer' }} onClick={handleGoBack}>Classification</span>
            <span>/</span>
            <span style={{ color: '#000', fontWeight: 600 }}>{projectName || projectId}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: '24px 40px', maxWidth: 1440, margin: '0 auto' }}>
      {status === "running_ocr" && <div style={{ color: "red", marginBottom: 16 }}>OCR Running...</div>}
      <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
      <ColorButton
        startIcon={<Add />}
        onClick={() => handleClickOpen('folder')}
        variant="contained"
        color="primary"
      >
        Upload Folder
      </ColorButton>
      <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
      <ColorButton
        startIcon={<Add />}
        onClick={() => handleClickOpen('files')}
        variant="contained"
        color="primary"
      >
        Upload Images
      </ColorButton>

      {status === "running_ocr" ?
        <>
          <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
          <ColorButton
            startIcon={<Replay />}
            onClick={refreshOcr}
            variant="contained"
            color="primary"
          >
            Redo Pending
          </ColorButton>
        </>
        : ""
      }
      {status === "running_ocr" ?
        <div style={{ float: 'right', marginRight: '90px' }}>
          <FormControlLabel control={<Switch
            checked={pending}
            onChange={togglePending}
            inputProps={{ 'aria-label': 'controlled' }}
            color="secondary" />} label="Only show pending" />
        </div>
        : ""
      }
      <Grid container className={classes.cards} >
        {loading ? <h3>Loading Images</h3> :
          images.length === 0
            ? <h3>No Images Available</h3>
            : images.map((image, index) => (
              image.ocr && pending ? <></> :
                <Grid container item xs={12} md={3} spacing={1} key={index}>
                  <Card className={classes.root}>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDeleteDialog(image.key)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                    {image.ocr ?
                      <Tooltip title="OCR Ready">
                        <IconButton>
                          <CheckCircle style={{ color: "green" }} />
                        </IconButton>
                      </Tooltip> : classificationType === 'image' ? '' :
                        <Tooltip title="OCR Pending">
                          <IconButton>
                            <FindReplace style={{ color: "orange" }} />
                          </IconButton>
                        </Tooltip>}
                    <CardContent>
                      <ImageContainer
                        src={image.key}
                        category='classification'
                      />
                    </CardContent>
                  </Card>
                </Grid>
            ))}
      </Grid>
      <Dialog open={openUpload} onClose={handleClose} aria-labelledby="upload">
        <FileUpload
          projectId={projectId}
          groupId={groupId}
          type={uploadType}
          uploadData='classification'
          classificationType={classificationType}
          getImages={getImages}
          setOpenUpload={setOpenUpload}
        />
      </Dialog>
      <Dialog open={openDelete} onClose={handleCloseDelete} aria-labelledby="delete">
        <DialogContent>
          Are you sure you want to delete ?
        </DialogContent>
        <DialogActions>
          <Button onClick={deleteImage} variant="contained" color="secondary">
            Yes
          </Button>
          <Button onClick={handleCloseDelete} variant="contained" color="default">
            No
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
  );
}

export default ClassificationProject;
